import type { ReactNode } from 'react';
import type { UserRole, UserStatus } from '../../api/types.ts';
import { cn } from './cn.ts';

export type BadgeTone = 'gray' | 'brand' | 'success' | 'warning' | 'error' | 'outline';

const TONES: Record<BadgeTone, string> = {
  gray: 'bg-tertiary text-tertiary',
  brand: 'bg-brand-primary text-brand-secondary',
  success: 'bg-success-primary text-success-primary',
  warning: 'bg-warning-primary text-warning-primary',
  error: 'bg-error-primary text-error-primary',
  outline: 'border border-primary text-tertiary',
};

export function Badge({
  tone = 'gray',
  children,
  className,
  mono,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  mono?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        TONES[tone],
        mono && 'font-mono',
        className,
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONES: Record<UserStatus, BadgeTone> = {
  invited: 'warning',
  active: 'success',
  deactivated: 'gray',
};

export function StatusBadge({ status }: { status: UserStatus }) {
  return <Badge tone={STATUS_TONES[status]}>{status}</Badge>;
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  staff: 'Staff',
  contractor: 'Contractor',
  portal_admin: 'Portal admin',
  portal_user: 'Portal user',
};

export function roleLabel(role: UserRole): string {
  return ROLE_LABELS[role] ?? role;
}

export function RoleBadge({ role }: { role: UserRole }) {
  const isStaffSide = role === 'admin' || role === 'staff' || role === 'contractor';
  return <Badge tone={isStaffSide ? 'brand' : 'outline'}>{roleLabel(role)}</Badge>;
}

/** `allow` and `deny` are the two words the whole permission model turns on. */
export function EffectBadge({ effect }: { effect: 'allow' | 'deny' }) {
  return (
    <Badge tone={effect === 'allow' ? 'success' : 'error'} mono>
      {effect}
    </Badge>
  );
}

export function CapabilityBadge({ capability }: { capability: string }) {
  return (
    <Badge tone="outline" mono>
      {capability}
    </Badge>
  );
}
